import { useEffect, useState } from "react";
import { formatTigerDateTime } from "../../utils/tigerTime";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Raw backend payload for version-update-available (snake_case from Rust). */
interface VersionUpdatePayload {
  config: string;
  /** Turbine display name for `config` (falls back to the config name). */
  turbine: string;
  config_time: { server: number; client: number };
  views_time: { server: number; client: number };
  bin_time: { server: number; client: number };
  manual_time: { server: number; client: number };
  certs_time: { server: number; client: number };
  update_config: boolean;
  update_views: boolean;
  update_bin: number; // 0 = none, else a download_bin target (BIN_TARGET_LABELS)
  update_manual: boolean;
  update_certs: boolean;
  /** The config is not installed on this PC at all (the site declares the
   *  turbine but its files were never downloaded here). The client timestamps
   *  are then the 1988 null sentinel, so show "not installed" instead. */
  config_missing?: boolean;
}

type DialogMode = "update" | "restart" | "backup";

interface DialogState {
  mode: DialogMode;
  payload?: VersionUpdatePayload;
  /** Folder currently being backed up (backup mode). */
  backupDir?: string;
}

/**
 * Listens for version-check backend events and presents an Acrylic-Glass dialog.
 *
 * - version-update-available → shows which components need updating with
 *   server/client timestamps; "Update" invokes confirm_version_download,
 *   "Skip" invokes skip_version_update.
 * - version-update-restart → replaces content with a restart prompt.
 * - version-backup-progress → shows the folder currently being backed up (the
 *   backup runs synchronously inside confirm_version_download, before the
 *   download starts).
 * - version-download-started → backup done / download started: close this dialog
 *   so the DownloadProgressDialog (download-progress) takes over immediately.
 * - version-check-complete → console.log only.
 *
 * Mounted once in App.tsx; renders nothing when not running inside Tauri.
 */

/** download_bin value -> label: lib_tiger_rs::tiger_util::BIN_TARGETS. */
const BIN_TARGET_LABELS: Record<number, string> = {
  2: "x86",
  3: "x64",
  4: "Linux x64",
  5: "ARM64",
  6: "Linux ARM64",
  7: "Linux x86",
  8: "Mac Intel",
  9: "Mac Apple Silicon",
  10: "Mac Intel",
};
export function VersionUpdateDialog() {
  const [state, setState] = useState<DialogState | null>(null);

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: Array<() => void> = [];

    void import("@tauri-apps/api/event").then(({ listen }) => {
      const attach = <P,>(event: string, handler: (payload: P) => void) =>
        listen<P>(event, (e) => handler(e.payload)).then((off) => {
          if (cancelled) off();
          else offs.push(off);
        });

      void attach<VersionUpdatePayload>("version-update-available", (p) => {
        setState({ mode: "update", payload: p });
      });

      void attach<null>("version-update-restart", () => {
        setState({ mode: "restart" });
      });

      void attach<string>("version-backup-progress", (dir) => {
        setState({ mode: "backup", backupDir: dir });
      });

      void attach<string>("version-download-started", () => {
        // Backup finished and the download has started: close this dialog so the
        // DownloadProgressDialog (driven by download-progress) takes over.
        setState(null);
      });

      void attach<null>("version-check-complete", () => {
        console.log("[VersionUpdate] version-check-complete — nothing to update.");
      });
    });

    return () => {
      cancelled = true;
      offs.forEach((off) => off());
    };
  }, []);

  if (!state) return null;

  const invokeCmd = async (cmd: string, args?: Record<string, unknown>) => {
    if (!isTauri()) return;
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke(cmd, args ?? {});
  };

  const onSkip = async () => {
    await invokeCmd("skip_version_update");
    setState(null);
  };

  const onUpdate = async (payload: VersionUpdatePayload) => {
    await invokeCmd("confirm_version_download", {
      config: payload.config,
      updateConfig: payload.update_config,
      updateViews: payload.update_views,
      updateBin: payload.update_bin,
      updateManual: payload.update_manual,
      updateCerts: payload.update_certs,
    });
    setState(null);
  };

  // Restart: applies the bin update (if any) and relaunches, then the backend
  // exits the app. Only resolves with an error if the relaunch couldn't start.
  const onRestart = async () => {
    try {
      await invokeCmd("restart_tiger");
    } catch (e) {
      console.error("restart_tiger failed:", e);
    }
  };

  // ── Restart prompt ──────────────────────────────────────────────────────────
  if (state.mode === "restart") {
    return (
      <div style={styles.overlay}>
        <div style={styles.dialog}>
          <div style={styles.header}>
            <span style={styles.title}>{getTigerResource("TIGER_VERSION_RESTART_REQUIRED")}</span>
          </div>
          <div style={styles.body}>
            <p style={styles.message}>
              {getTigerResource("TIGER_VERSION_RESTART_MSG")}
            </p>
          </div>
          <div style={styles.footer}>
            <button className="btn-acrylic-secondary" onClick={() => setState(null)}>
              {getTigerResource("TIGER_VERSION_LATER")}
            </button>
            <button className="btn-acrylic-primary" onClick={() => void onRestart()}>
              {getTigerResource("TIGER_VERSION_RESTART_NOW")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Backup in progress ──────────────────────────────────────────────────────
  if (state.mode === "backup") {
    return (
      <div style={styles.overlay}>
        <div style={styles.dialog}>
          <div style={styles.header}>
            <span style={styles.title}>{getTigerResource("TIGER_VERSION_BACKUP_TITLE")}</span>
          </div>
          <div style={styles.body}>
            <p style={styles.message}>
              {getTigerResource("TIGER_VERSION_BACKING_UP")} <strong>{state.backupDir}</strong>&hellip;
            </p>
            <div style={styles.barOuter}>
              <div className="tiger-indeterminate-bar" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Update available ────────────────────────────────────────────────────────
  const payload = state.payload!;
  const fmt = (secs: number) => formatTigerDateTime(secs);

  interface UpdateRow {
    label: string;
    server: number;
    client: number;
    /** Replaces the client timestamp (a never-installed config has none). */
    clientLabel?: string;
  }
  // A config that isn't installed here reports the 1988 null sentinel as its
  // client time for config + views; showing that date would read as a real
  // (very old) install.
  const missingLabel = payload.config_missing
    ? getTigerResource("TIGER_VERSION_NOT_INSTALLED")
    : undefined;
  const rows: UpdateRow[] = [];
  if (payload.update_config)
    rows.push({ label: getTigerResource("TIGER_VERSION_CONFIGURATION"), server: payload.config_time.server, client: payload.config_time.client, clientLabel: missingLabel });
  if (payload.update_views)
    rows.push({ label: getTigerResource("TIGER_VERSION_MSG_VIEWS"), server: payload.views_time.server, client: payload.views_time.client, clientLabel: missingLabel });
  if (payload.update_bin !== 0)
    rows.push({
      label: tigerFormat(getTigerResource("TIGER_VERSION_BINARY"), BIN_TARGET_LABELS[payload.update_bin] ?? "x86"),
      server: payload.bin_time.server,
      client: payload.bin_time.client,
    });
  if (payload.update_manual)
    rows.push({ label: getTigerResource("TIGER_VERSION_MSG_MANUAL"), server: payload.manual_time.server, client: payload.manual_time.client });
  if (payload.update_certs)
    rows.push({ label: getTigerResource("TIGER_VERSION_MSG_CERTS"), server: payload.certs_time.server, client: payload.certs_time.client });

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{tigerFormat(getTigerResource("TIGER_VERSION_UPDATE_TITLE"), payload.turbine || payload.config)}</span>
          <button aria-label={getTigerResource("TIGER_RES_CLOSE")} className="acrylic-close-btn" onClick={onSkip}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <p style={styles.message}>
            {getTigerResource(
              payload.config_missing
                ? "TIGER_VERSION_MISSING_MSG_PRE"
                : "TIGER_VERSION_UPDATE_MSG_PRE",
            )}
            <strong>{getTigerResource("TIGER_GRAPH_DISTRIB_UPD")}</strong>
            {getTigerResource("TIGER_VERSION_UPDATE_MSG_MID")}
            <strong>{getTigerResource("TIGER_RES_SKIP")}</strong>
            {getTigerResource("TIGER_VERSION_UPDATE_MSG_POST")}
          </p>

          {rows.length > 0 && (
            <div style={styles.card}>
              <div style={styles.tableHeader}>
                <span style={styles.colComponent}>{getTigerResource("TIGER_VERSION_COMPONENT")}</span>
                <span style={styles.colTime}>{getTigerResource("TIGER_VERSION_SERVER")}</span>
                <span style={styles.colTime}>{getTigerResource("TIGER_VERSION_CLIENT")}</span>
              </div>
              {rows.map((row) => (
                <div key={row.label} style={styles.tableRow}>
                  <span style={styles.colComponent}>{row.label}</span>
                  <span style={styles.colTime}>{fmt(row.server)}</span>
                  <span style={styles.colTime}>{row.clientLabel ?? fmt(row.client)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={onSkip}>
            {getTigerResource("TIGER_RES_SKIP")}
          </button>
          <button className="btn-acrylic-primary" onClick={() => void onUpdate(payload)}>
            {getTigerResource("TIGER_GRAPH_DISTRIB_UPD")}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  overlay: {
    ...acrylicStyles.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    background: acrylicTokens.surface,
    backdropFilter: acrylicTokens.surfaceBlur,
    WebkitBackdropFilter: acrylicTokens.surfaceBlur,
    border: acrylicTokens.surfaceBorder,
    borderRadius: acrylicTokens.surfaceRadius,
    width: 520,
    maxWidth: "95vw",
    boxShadow: acrylicTokens.surfaceShadow,
    overflow: "hidden" as const,
    display: "flex",
    flexDirection: "column" as const,
    fontFamily: acrylicTokens.font,
    color: acrylicTokens.textPrimary,
  },
  message: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.5,
    color: acrylicTokens.textSecondary,
  },
  barOuter: {
    position: "relative" as const,
    height: 20,
    marginTop: 12,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(120,130,150,0.35)",
    borderRadius: 7,
    overflow: "hidden",
  },
  tableHeader: {
    display: "flex",
    gap: 8,
    paddingBottom: 6,
    borderBottom: `1px solid ${acrylicTokens.separator}`,
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    color: acrylicTokens.textSecondary,
  },
  tableRow: {
    display: "flex",
    gap: 8,
    fontSize: 13,
    color: acrylicTokens.textPrimary,
    paddingTop: 4,
    paddingBottom: 4,
    borderBottom: acrylicTokens.rowDivider,
  },
  colComponent: {
    flex: "0 0 130px",
    fontWeight: 500,
  } as const,
  colTime: {
    flex: 1,
    fontVariantNumeric: "tabular-nums",
    fontSize: 12,
    color: acrylicTokens.textSecondary,
  } as const,
} as const;
